import { DateTime } from "luxon";

const CITI_HEADER_ROW_INDEX = 0;
const CITI_HEADER_START_COLUMN_INDEX = 0;

const CITI_HEADERS = ["Status", "Date", "Description", "Debit", "Credit"];

const CITI_DEFAULT_DETAIL_FIELDS = [];

const CITI_DETAIL_FIELDS = [];

const CITI_DETAIL_FIELD_ALIASES = {};

function normalizeHeaderName(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeInstitutionName(name) {
  return String(name || "")
    .trim()
    .toLowerCase();
}

function normalizeDetailFieldName(fieldName) {
  const normalized = normalizeHeaderName(fieldName);

  if (CITI_DETAIL_FIELD_ALIASES[normalized]) {
    return CITI_DETAIL_FIELD_ALIASES[normalized];
  }

  const matchedDetailField = CITI_DETAIL_FIELDS.find(
    (field) => normalizeHeaderName(field) === normalized
  );

  return matchedDetailField || null;
}

function isCitiDataRowEmpty(row, headerIndexes) {
  return headerIndexes.every((idx) => {
    const cellValue = row?.[idx];
    return String(cellValue ?? "").trim() === "";
  });
}

function buildHeaderIndexes(headerRow) {
  const headerIndexes = {};
  const normalizedHeaderRow = headerRow.map((cell) =>
    normalizeHeaderName(cell)
  );

  CITI_HEADERS.forEach((header, offset) => {
    const expectedIndex = CITI_HEADER_START_COLUMN_INDEX + offset;
    const expectedHeader = normalizeHeaderName(header);
    const foundHeader = normalizedHeaderRow[expectedIndex];

    if (foundHeader !== expectedHeader) {
      throw new Error(
        `Unexpected Citi file format at row ${
          CITI_HEADER_ROW_INDEX + 1
        }, column ${expectedIndex + 1}. Expected "${header}", found "${
          headerRow?.[expectedIndex] ?? ""
        }".`
      );
    }

    headerIndexes[header] = expectedIndex;
  });

  return headerIndexes;
}

function safeCellValue(row, index) {
  return String(row?.[index] ?? "").trim();
}

function toIsoDateFromCiti(rawDateValue, rowNumber) {
  const rawDate = String(rawDateValue ?? "").trim();
  // Try to parse as M/d/yyyy first
  let parsed = DateTime.fromFormat(rawDate, "M/d/yyyy");
  if (parsed.isValid) {
    return parsed.toISODate();
  }
  // If not valid, try to parse as Excel serial number (integer or float)
  const excelSerial = Number(rawDate);
  if (!isNaN(excelSerial) && excelSerial > 59) {
    // Excel's leap year bug: 1900-02-29 is day 60
    // Excel serial dates: 1 = 1900-01-01, but JS Date epoch is 1970-01-01
    // Luxon: use DateTime.fromObject({ year, month, day })
    // Excel's day 1 is 1899-12-31 in JS Date (Excel bug: 1900 is not a leap year)
    // So, base date is 1899-12-30 for Excel serials
    const base = DateTime.fromISO("1899-12-30");
    parsed = base.plus({ days: excelSerial });
    if (parsed.isValid) {
      return parsed.toISODate();
    }
  }
  throw new Error(
    `Invalid Citi date at row ${rowNumber}. Expected M/d/yyyy or Excel serial, found "${rawDate}".`
  );
}

function pickRequestedDetails(row, headerIndexes, detailsToInclude) {
  const requestedFields =
    Array.isArray(detailsToInclude) && detailsToInclude.length > 0
      ? detailsToInclude
      : CITI_DEFAULT_DETAIL_FIELDS;

  return requestedFields.reduce((acc, fieldName) => {
    const matchingHeader = CITI_HEADERS.find(
      (header) => normalizeHeaderName(header) === normalizeHeaderName(fieldName)
    );

    if (!matchingHeader) {
      return acc;
    }

    const value = safeCellValue(row, headerIndexes[matchingHeader]);
    if (!value) {
      return acc;
    }

    return {
      ...acc,
      [matchingHeader]: value,
    };
  }, {});
}

export const citiAdapter = {
  name: "citi",
  institutionAliases: ["citi", "citi bank", "citibank"],
  supportedDetailFields: [...CITI_DETAIL_FIELDS],
  canHandleInstitutionName(institutionName) {
    const normalized = normalizeInstitutionName(institutionName);
    return this.institutionAliases.includes(normalized);
  },
  validateDetailsToInclude(detailsToInclude = []) {
    const requestedFields =
      Array.isArray(detailsToInclude) && detailsToInclude.length > 0
        ? detailsToInclude
        : CITI_DEFAULT_DETAIL_FIELDS;

    const normalized = [];
    const invalid = [];
    const seenNormalized = new Set();

    requestedFields.forEach((fieldName) => {
      const normalizedFieldName = normalizeDetailFieldName(fieldName);

      if (!normalizedFieldName) {
        invalid.push(String(fieldName));
        return;
      }

      if (!seenNormalized.has(normalizedFieldName)) {
        seenNormalized.add(normalizedFieldName);
        normalized.push(normalizedFieldName);
      }
    });

    return {
      normalized,
      invalid,
      allowed: [...CITI_DETAIL_FIELDS],
    };
  },
  extractTransactions(rows, options = {}) {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    const headerRow = rows[CITI_HEADER_ROW_INDEX] || [];
    const headerIndexes = buildHeaderIndexes(headerRow);
    const transactions = [];

    for (
      let rowIndex = CITI_HEADER_ROW_INDEX + 1;
      rowIndex < rows.length;
      rowIndex += 1
    ) {
      const row = rows[rowIndex] || [];
      console.log("Processing Citi row", rowIndex + 1, row);

      if (isCitiDataRowEmpty(row, Object.values(headerIndexes))) {
        break;
      }

      const details = pickRequestedDetails(
        row,
        headerIndexes,
        options.detailsToInclude
      );

      const rowNumber = rowIndex + 1;
      const isoDate = toIsoDateFromCiti(row[headerIndexes.Date], rowNumber);

      // Unlike AMEX, Citi has two rows for amounts: Debit and Credit. We will treat Debit as negative and Credit as positive.
      const rawDebit = safeCellValue(row, headerIndexes.Debit);
      const rawCredit = safeCellValue(row, headerIndexes.Credit);

      let amount = "0";
      if (rawDebit && rawCredit) {
        throw new Error(
          `Invalid Citi transaction at row ${rowNumber}. Both Debit and Credit fields have values.`
        );
      } else if (rawDebit) {
        // Negate since we want debits to be negative amounts (purchases)
        amount = String(-1 * parseFloat(rawDebit));
      } else if (rawCredit) {
        // Negate since we want credits to be positive amounts (payments and refunds)
        amount = String(-1 * parseFloat(rawCredit));
      }

      transactions.push({
        rowNumber,
        date: isoDate,
        amount: amount,
        description: row[headerIndexes.Description],
        details,
      });
    }

    return transactions;
  },
};
